import { useCallback } from 'react';
import { useAuth } from 'react-oidc-context';

const API_URL = import.meta.env.VITE_API_URL;
const IS_DEV_AUTH = import.meta.env.VITE_AUTH_MODE === 'dev';

/**
 * Browser's IANA timezone name (e.g. "Pacific/Honolulu") plus a numeric
 * offset fallback. Sent on every API request so backend handlers can SET
 * the MySQL session time_zone — fixes "today" rollover for non-UTC users.
 */
function timezoneHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) headers['X-User-Timezone'] = tz;
  } catch {
    // Older browsers without resolvedOptions().timeZone — skip the IANA
    // header and rely on the offset fallback below.
  }
  // JS getTimezoneOffset() returns minutes WEST of UTC (HST → +600).
  // Negate to get the conventional signed offset (HST → -600).
  headers['X-User-Timezone-Offset-Minutes'] = String(
    -new Date().getTimezoneOffset(),
  );
  return headers;
}

/** Hook returning a `fetch` wrapper that attaches the current Cognito access token.
 *  In sandbox dev-auth mode the Authorization header is omitted — the API
 *  Gateway has no authorizer and Lambdas resolve every request to DEV_USER_SUB. */
export function useApi() {
  const auth = useAuth();
  return useCallback(
    async (path: string, init: RequestInit = {}): Promise<Response> => {
      const headers: Record<string, string> = {
        ...(init.headers as Record<string, string> | undefined),
        'Content-Type': 'application/json',
        ...timezoneHeaders(),
      };
      if (!IS_DEV_AUTH) {
        const token = auth.user?.access_token;
        if (!token) throw new Error('Not authenticated');
        headers.Authorization = `Bearer ${token}`;
      }
      return fetch(`${API_URL}${path}`, { ...init, headers });
    },
    [auth.user?.access_token],
  );
}